import LoadingIndicator from '../loadingindicator';
import { isTouchDevice } from '../user/device-info';
import { calculateElementHeaderOffset } from '../header/offset';

class InfoSearch {
    _form: HTMLFormElement;
    _queryInput: HTMLInputElement;
    _minimumQueryLength: number = 3;
    _maximumQueryLength: number = 50;
    _loadingIndicator: LoadingIndicator;
    _typeaheadInput: HTMLInputElement;
    _searchbar: HTMLElement;
    _scrollOffset: number | null = null;
    _scrollTimeout: number | null = null;

    constructor( form: HTMLFormElement ) {
        this._form = form;
        this._queryInput = this._form.querySelector( 'input[name="q"]' ) as HTMLInputElement;
        this._form.addEventListener( 'submit', this._onSubmit.bind( this ) );
        this._loadingIndicator = new LoadingIndicator( ( this._form.parentElement as HTMLElement ).querySelector( '.typeahead' ) as HTMLElement );
        this._typeaheadInput = this._form.querySelector( 'input[name=q]' ) as HTMLInputElement;
        this._searchbar = this._form.querySelector( '.searchhero__typeahead' ) as HTMLElement;

        if ( this._typeaheadInput && this._searchbar && isTouchDevice() ) {
            window.addEventListener( 'scroll', this._onScroll.bind( this ), { passive: true } );
            this._typeaheadInput.addEventListener( 'focus', this._onTypeaheadFocus.bind( this ) );
        }
    }

    _onScroll( event: Event ): void {
        if ( this._scrollTimeout !== null ) {
            window.clearTimeout( this._scrollTimeout );
        }

        this._scrollTimeout = window.setTimeout( () => {
            this._scrollOffset = calculateElementHeaderOffset( this._searchbar );
        }, 200 );
    }

    _onTypeaheadFocus( event: Event ): void {
        if ( !this._scrollOffset ) {
            this._scrollOffset = calculateElementHeaderOffset( this._searchbar );
        }

        if ( !this._scrollOffset ) {
            return;
        }

        window.setTimeout( () => {
            window.scrollTo( {
                top: this._scrollOffset as number,
            } );
        } );
    }

    _onSubmit( event: Event ): void {
        event.preventDefault();

        this._form.classList.add( 'typeahead--loading' );

        this._loadingIndicator.show();

        const query = this._sanitiseQuery( this._queryInput.value );

        const valid = this._validateQuery( query );

        if ( !valid ) {
            this._form.classList.remove( 'typeahead--loading' );
            this._loadingIndicator.hide();
            return;
        }

        this._form.submit();
    }

    _sanitiseQuery( query: string ): string {
        query = query.trim();
        query = query.replaceAll( /\s{2,}/g, ' ' );
        return query;
    }

    _validateQuery( query: string ): boolean {
        // query is not too short
        if ( query.length < this._minimumQueryLength ) {
            return false;
        }

        // query is not overly long
        if ( query.length > this._maximumQueryLength ) {
            return false;
        }

        // query isn't only numbers, punctuation, white-space
        if ( /^([0-9]|\W|\s)*$/.test( query ) ) {
            return false;
        }

        const uniqueChars: string[] = [];

        query.split( '' ).filter( ( char ) => /^[a-zA-Z]$/.test( char ) ).forEach( ( char: string ) => {
            if ( uniqueChars.includes( char ) ) {
                return;
            }
            uniqueChars.push( char );
        } );

        if ( uniqueChars.length < 3 ) {
            return false;
        }

        return true;
    }
}

export default function initInformationSearch(): void {
    const form = document.querySelector<HTMLFormElement>( '#article-search-form' );

    if ( form ) {
        new InfoSearch( form );
    }
}
